import { disallowWordCharacters } from '@src/common';
import { LayoutColors } from '@src/core/theme';
import {
  BodyLargeBold,
  BodyRegularSemiBold,
  BodySmall,
  BodySmallMedium,
  BodyRegularMedium,
} from '@src/fonts/typography/Typography';
import useTranslate from '@src/i18n/useTranslate';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { device } from '@src/common/breakpoints';
import Input from '@src/components/ui/input';

import { PaymentMethodFormProps } from '@src/features/payments/types';
import { track } from '@src/services/analytics';
import { useAppSelector } from '@src/core/hooks';
import { buyerIsForeign } from '@src/features/customers';
import { CardBrandArt } from '@src/features/payments/components/CardBrandArt';
import NationalitySelect from '@src/components/ui2/NationalitySelect';
import { CountryCode, getCepAddress } from '@src/common/auth';
import BackdropLoading from '@src/components/ui2/BackdropLoading';
import { updateDOMField } from '@src/common/updateDomField';
import BrazilianStateSelect from '@src/components/ui2/BrazilianStateSelect';

export const CardPaymentForm = (props: PaymentMethodFormProps) => {
  const { paymentOption, handleSubmit, errors } = props;
  const formRef = useRef<HTMLFormElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { i18n } = useTranslate();

  const isForeign = useAppSelector(buyerIsForeign);
  const requiresExternalAuthentication = paymentOption?.requiresExternalAuthentication;

  const getError = useCallback(
    (field: string) => {
      const valName = `paymentCard.${field}`;
      return errors[valName] ? errors[valName]!.join(', ') : null;
    },
    [errors],
  );

  const getCardNumberError = useMemo(() => {
    const regularError = getError('cardNumber');
    const paymentCardError = errors['paymentCard']?.join(', ');

    return regularError ?? paymentCardError;
  }, [getError]);

  const handleCepChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const zipCode = event.target.value.replace(/[^0-9]/g, '');

    if (zipCode.length === 8) {
      getAddressByCep(zipCode);
    }
  }, []);

  const getAddressByCep = useCallback(async (zipCode: string) => {
    if (isForeign) return;

    try {
      setIsLoading(true);
      const brasilApiAddress = await getCepAddress((zipCode ?? '').replace(/[^0-9]/g, ''));

      if (brasilApiAddress) {
        const form = formRef.current;

        updateDOMField({ fieldName: 'countryCode', value: CountryCode.BRAZIL, type: 'select', element: form });

        if (brasilApiAddress.state) {
          updateDOMField({
            fieldName: 'state',
            value: brasilApiAddress.state,
            type: isForeign ? 'input' : 'select',
            element: form,
          });
        }
        if (brasilApiAddress.city) {
          updateDOMField({ fieldName: 'city', value: brasilApiAddress.city, element: form });
        }
        if (brasilApiAddress.district) {
          updateDOMField({ fieldName: 'district', value: brasilApiAddress.district, element: form });
        }
        if (brasilApiAddress.street) {
          updateDOMField({ fieldName: 'street', value: brasilApiAddress.street, element: form });
        }
        if (brasilApiAddress.complement) {
          updateDOMField({ fieldName: 'complement', value: brasilApiAddress.complement, element: form });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    track('checkoutPaymentFormCard');
  }, []);

  return (
    <CardPayment ref={formRef} onSubmit={handleSubmit}>
      <input type='hidden' name='paymentMethodId' value={paymentOption?.paymentMethod.id} />
      <CardPaymentTitle>{paymentOption?.paymentMethod.name}</CardPaymentTitle>
      <PaymentSoftdescriptor>
        {i18n('checkout_method_softdescriptor')} {paymentOption?.softDescriptor}
      </PaymentSoftdescriptor>
      <Input
        name='holder'
        label={i18n('checkout_nameAsInCard')}
        hint={getError('holder') ? '' : i18n('checkout_nameAsInCardHint')}
        error={getError('holder')}
      />

      {!isForeign ? (
        <TwoInputs>
          <Input
            name='holderIdentity'
            mask={['cpf', 'cnpj']}
            label={i18n('checkout_card_owner_cpf_cnpj')}
            error={getError('holderIdentity')}
          />

          {!requiresExternalAuthentication ? (
            <Input
              required
              name='zipCode'
              label={i18n('checkout_addressZipCode')}
              mask={'br_postal_code'}
              error={getError('billingAddress.zipCode')}
              onKeyPress={disallowWordCharacters}
              disabled={isLoading}
            />
          ) : null}
        </TwoInputs>
      ) : null}

      <TwoInputs>
        <Input
          name='cardNumber'
          mask='card_number'
          label={i18n('checkout_cardNumber')}
          error={getCardNumberError}
          onKeyPress={disallowWordCharacters}
          hint={
            <Brands>
              {paymentOption?.brands?.map((brand) => (
                <CardBrandArt code={brand.code} key={brand.code} />
              ))}
            </Brands>
          }
        />
        <Input
          name='securityCode'
          mask='security_card_code'
          label={i18n('checkout_securityCode')}
          error={getError('securityCode')}
          type='text'
        />
      </TwoInputs>

      <TwoInputs>
        <SingleInput>
          <Label>{i18n('checkout_valid_date')}</Label>

          <Input
            name='cardValidity'
            placeholder='__/__'
            mask='payment_card_validity'
            error={getError('validityMonth') ?? getError('validityYear')}
          />
        </SingleInput>

        <SingleInput>
          <Label>{i18n('checkout_paymentIn')}</Label>
          <Select name='installments' defaultValue={paymentOption?.installmentOptions[0]?.division}>
            {paymentOption?.installmentOptions.map(({ division, label }) => (
              <option key={division} value={division}>
                {label}
              </option>
            ))}
          </Select>
        </SingleInput>
      </TwoInputs>

      {requiresExternalAuthentication ? (
        <AddressWrapper>
          <AddressTitle>{i18n('checkout_addressTitle')}</AddressTitle>

          <TwoInputs>
            <SingleInput>
              <NationalitySelect
                name='countryCode'
                showFlag={false}
                label={i18n('checkout_addressCountry')}
                disabled={isLoading}
              />
            </SingleInput>

            <Input
              required
              name='zipCode'
              label={i18n('checkout_addressZipCode')}
              mask={!isForeign ? 'br_postal_code' : undefined}
              error={getError('billingAddress.zipCode')}
              onKeyPress={disallowWordCharacters}
              onChange={handleCepChange}
              disabled={isLoading}
            />
          </TwoInputs>

          <TwoInputs>
            {isForeign ? (
              <Input
                required
                name='state'
                label={i18n('checkout_addressState')}
                error={getError('billingAddress.state')}
                disabled={isLoading}
                maxLength={2}
                hint={i18n('checkout_addressStateHint')}
              />
            ) : (
              <BrazilianStateSelect
                name='state'
                label={i18n('checkout_addressState')}
                error={getError('billingAddress.state')}
                disabled={isLoading}
                fullWidth
              />
            )}

            <Input
              required
              name='city'
              label={i18n('checkout_addressCity')}
              error={getError('billingAddress.city')}
              disabled={isLoading}
            />
          </TwoInputs>

          <TwoInputs>
            <Input
              required
              name='district'
              label={i18n('checkout_addressDistrict')}
              error={getError('billingAddress.district')}
              disabled={isLoading}
            />

            <Input
              required
              name='street'
              label={i18n('checkout_addressStreet')}
              error={getError('billingAddress.street')}
              disabled={isLoading}
            />
          </TwoInputs>

          <TwoInputs>
            <Input
              required
              name='number'
              label={i18n('checkout_addressNumber')}
              error={getError('billingAddress.number')}
              disabled={isLoading}
            />

            <Input
              name='complement'
              label={i18n('checkout_addressComplement')}
              error={getError('billingAddress.complement')}
              disabled={isLoading}
            />
          </TwoInputs>
        </AddressWrapper>
      ) : null}

      <CompletePurchase data-cy='completePurchase'>{i18n('checkout_complete_purchase')}</CompletePurchase>

      {isLoading ? <BackdropLoading /> : null}
    </CardPayment>
  );
};

const CardPayment = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardPaymentTitle = styled.h4`
  ${BodyLargeBold};
  color: ${LayoutColors.justBlue};
`;

const Brands = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-top: 4px;
  img {
    width: 25px;
    margin-right: 4px;
  }
`;

const Label = styled.label`
  ${BodySmall};
`;

const SingleInput = styled.div`
  width: 100%;
  color: ${LayoutColors.blackDark};
  ${BodySmall};
`;

const TwoInputs = styled.div`
  display: flex;
  align-items: flex-start;
  flex-flow: column;
  gap: 16px;

  ${device.smallTablet} {
    flex-flow: row;
    gap: 0 15px;
  }
`;

const AddressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AddressTitle = styled.h5`
  ${BodyRegularMedium};
  color: ${LayoutColors.blackDark};
`;

const CompletePurchase = styled.button`
  width: 100%;
  background-color: ${LayoutColors.successDark};
  height: 44px;
  ${BodyRegularSemiBold};
  border-radius: 4px;
  padding: 8px;
  margin-top: 16px;
  color: ${LayoutColors.whiteLight};
  border-style: none;
  cursor: pointer;
`;

const InputSelectStyle = css`
  width: 100%;
  margin-bottom: 4px;
  border-radius: 4px;
  background-color: ${LayoutColors.whiteLight};
  border: 1px solid ${LayoutColors.blackLight};
  padding: 12px;
  height: 46px;
`;

const Select = styled.select`
  ${InputSelectStyle};

  -webkit-appearance: none;
  -moz-appearance: none;
  background: white;
  background-image: url("data:image/svg+xml;utf8,<svg fill='gray' height='30' viewBox='0 0 24 24' width='40' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>");
  background-repeat: no-repeat;
  background-position-x: 100%;
  background-position-y: 6px;
  cursor: pointer;

  option {
    background-color: white !important;
    color: ${LayoutColors.blackMain} !important;
  }
`;

const PaymentSoftdescriptor = styled.span`
  width: 100%;
  ${BodySmallMedium};
  color: ${LayoutColors.justBlue};
  background-color: ${LayoutColors.justBlueLigth};
  padding: 8px;
  border-radius: 4px;
`;
